import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { MODEL_OPTIONS, PREMIUM_TASK_DEFAULTS } from '../lib/modelCatalog';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LectureAiChip, MasteryJourneyPanel, ModelBadge, SurfaceHero } from '../components/design-kit';
import { BookOpenCheck, Loader2, RefreshCw, Sparkles } from 'lucide-react';

export default function MasteryJourneyPage() {
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [journeyResult, setJourneyResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [masteryModel, setMasteryModel] = useState(PREMIUM_TASK_DEFAULTS.mastery_planner);

  const selectedSubject = useMemo(
    () => subjects.find((subject) => subject.id === selectedSubjectId) || null,
    [selectedSubjectId, subjects]
  );

  const loadSubjects = useCallback(async () => {
    try {
      const subjectData = await api.getSubjects();
      setSubjects(subjectData || []);
      if (!selectedSubjectId && subjectData?.length) {
        setSelectedSubjectId(subjectData[0].id);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to load subjects');
    } finally {
      setLoading(false);
    }
  }, [selectedSubjectId]);

  const loadJourney = async (subjectId) => {
    if (!subjectId) return;
    try {
      const result = await api.getSubjectMasteryJourney(subjectId);
      setJourneyResult(result);
    } catch (error) {
      toast.error(error.message || 'Failed to load mastery journey');
    }
  };

  useEffect(() => {
    loadSubjects();
  }, [loadSubjects]);

  useEffect(() => {
    if (selectedSubjectId) {
      loadJourney(selectedSubjectId);
    }
  }, [selectedSubjectId]);

  const handleRefresh = async () => {
    if (!selectedSubjectId) return;
    setRefreshing(true);
    try {
      const result = await api.refreshSubjectMasteryJourney(selectedSubjectId, masteryModel);
      setJourneyResult(result);
      toast.success('Mastery journey refreshed');
    } catch (error) {
      toast.error(error.message || 'Failed to refresh mastery journey');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto bg-background">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
        <SurfaceHero
          eyebrow="Mastery"
          title="Pedantic subject journeys"
          detail="Turn lectures, notes, uploads, and mistakes into one strict, updatable learning path. Each refresh rewrites the subject journey with source-backed phases and explicit blindspots."
          aside={(
            <>
              <div className="min-w-[220px]">
                <Select value={masteryModel} onValueChange={setMasteryModel}>
                  <SelectTrigger className="h-10 rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODEL_OPTIONS.filter((model) => model.family !== 'realtime').map((model) => (
                      <SelectItem key={model.value} value={model.value}>
                        {model.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleRefresh} disabled={!selectedSubjectId || refreshing} className="rounded-xl">
                {refreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Refresh journey
              </Button>
            </>
          )}
        />

        <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
          <Card className="border-border/60 bg-card/70 shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-serif text-xl">
                <BookOpenCheck className="h-5 w-5" />
                Subjects
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading subjects...
                </div>
              ) : subjects.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border p-6 text-sm text-muted-foreground">
                  Create a subject first to generate a mastery journey.
                </div>
              ) : (
                subjects.map((subject) => {
                  const active = selectedSubjectId === subject.id;
                  return (
                    <button
                      key={subject.id}
                      type="button"
                      onClick={() => setSelectedSubjectId(subject.id)}
                      className={`w-full rounded-[22px] border px-4 py-4 text-left transition-[transform,border-color,background-color] duration-200 ease-out motion-reduce:transition-none ${
                        active ? 'border-primary/30 bg-primary/5' : 'border-border/70 bg-background/70 hover:-translate-y-[1px] hover:border-border'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-medium text-foreground">{subject.name}</p>
                          {subject.course_code ? <p className="mt-1 text-xs text-muted-foreground">{subject.course_code}</p> : null}
                        </div>
                        <LectureAiChip label={`${subject.lecture_count || 0} lectures`} tone="subject" />
                      </div>
                    </button>
                  );
                })
              )}
            </CardContent>
          </Card>

          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <LectureAiChip icon={Sparkles} label="Shared subject context" tone="tool" active />
              <ModelBadge modelId={masteryModel} provider={masteryModel.startsWith('grok-') ? 'xai' : 'openai'} family={masteryModel === 'gpt-realtime' ? 'realtime' : 'reasoning'} />
            </div>
            <MasteryJourneyPanel journey={journeyResult?.journey} subject={selectedSubject} />
          </div>
        </div>
      </div>
    </div>
  );
}
